import { useEffect, useState } from "react";
import { Search, Loader2, Link2Off } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { fmtDateTime } from "@/lib/app-utils";
import JourneyTimeline from "@/components/JourneyTimeline";
import { Section, DataTable, Kpi, pctFmt } from "./_shared";
import { BarChartC } from "../_charts";

function CustomerList({ params, onOpen }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  async function load(query = q) {
    setRows(null);
    try {
      const r = await api.get("/omni-reports/journey/customers", { params: { ...params, q: query } });
      setRows(r.data.rows);
    } catch (e) { toast.error(formatErr(e)); setRows([]); }
  }
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, [params]);
  return (
    <Section title="Customer journeys" subtitle="Click a customer to see their full cross-channel timeline" testid="omni-cust-card"
      right={<div className="relative w-64"><Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search name, phone, email, handle" className="pl-8 h-9" data-testid="omni-cust-search" /></div>}>
      {!rows ? <div className="py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div> : (
        <div className="overflow-x-auto max-h-[480px]">
          <table className="w-full text-sm" data-testid="omni-cust-table">
            <thead className="bg-muted/60 sticky top-0"><tr>
              {["Customer", "Journey", "Touches", "Last activity"].map((h) => <th key={h} className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center italic text-muted-foreground">No customers in this range.</td></tr>}
              {rows.map((r, i) => (
                <tr key={r.key} onClick={() => onOpen(r)} className="border-t border-border/50 hover:bg-muted/30 cursor-pointer" data-testid={`omni-cust-row-${i}`}>
                  <td className="px-3 py-1.5">
                    <div className="font-medium flex items-center gap-1.5">{r.name}{!r.linked && <Badge variant="outline" className="text-[10px] gap-1"><Link2Off className="w-3 h-3" />Unlinked</Badge>}</div>
                    <div className="text-xs text-muted-foreground">{r.phone || r.email || r.handle}</div>
                  </td>
                  <td className="px-3 py-1.5 text-xs">{r.channels.join(" → ")}</td>
                  <td className="px-3 py-1.5 font-mono">{r.touches}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{fmtDateTime(r.last_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

export default function OmniJourneys({ data, params }) {
  const [open, setOpen] = useState(null);
  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid="omni-journeys">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Customers with activity" value={t.customers} testid="omni-j-customers" />
        <Kpi label="Used 2+ channels" value={t.omnichannel_customers} hint={`${t.omnichannel_share ?? 0}% of customers`} testid="omni-j-multi" />
        <Kpi label="Top first channel" value={data.first_touch?.[0]?.channel} testid="omni-j-first" />
        <Kpi label="Top last channel" value={data.last_touch?.[0]?.channel} testid="omni-j-last" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Section title="First contact channel" testid="omni-j-first-chart">
          <BarChartC data={data.first_touch || []} xKey="channel" yKeys={[{ id: "customers", label: "Customers" }]} height={220} />
        </Section>
        <Section title="Latest contact channel" testid="omni-j-last-chart">
          <BarChartC data={data.last_touch || []} xKey="channel" yKeys={[{ id: "customers", label: "Customers" }]} height={220} />
        </Section>
        <Section title="Channels used per customer" testid="omni-j-breadth-chart">
          <BarChartC data={data.channels_per_customer || []} xKey="channels" yKeys={[{ id: "customers", label: "Customers" }]} height={220} />
        </Section>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Section title="Most common journey paths" testid="omni-j-paths-card">
          <DataTable columns={data.columns} rows={data.rows || []} format={{ share: pctFmt }} testid="omni-j-paths" />
        </Section>
        <Section title="Channel switches" subtitle="Where customers go next" testid="omni-j-trans-card">
          <DataTable columns={[{ id: "from", label: "From" }, { id: "to", label: "To" }, { id: "count", label: "Switches" }]}
            rows={data.transitions || []} testid="omni-j-trans" empty="No channel switches in this range." />
        </Section>
      </div>
      <CustomerList params={params} onOpen={setOpen} />
      <Sheet open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="omni-journey-sheet">
          <SheetHeader><SheetTitle>{open?.name} {open && <span className="text-sm font-normal text-muted-foreground">{open.phone || open.email || open.handle}</span>}</SheetTitle></SheetHeader>
          <div className="mt-4">{open && <JourneyTimeline contactId={open.contact_id} identity={open.identity} />}</div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
